/**
 * @file components/TagEditor.js
 * @description Customer Butler - contact tagging UI, shared by the customer
 * list, the customer record, and anywhere else a person gets tagged.
 *
 * James asked for BOTH kinds of tagging, so this component does both in one box:
 *   1. Free-form - type anything and press Enter. Nothing validates the text
 *      against a list, because the whole point is that the office can invent a
 *      tag at 7am without asking anyone.
 *   2. Suggested - the company's curated list (owner-editable in CRM Setup)
 *      renders as one-click chips, so the tags everybody uses stay spelled the
 *      same way instead of drifting into "VIP", "vip" and "V.I.P.".
 *
 * Exports:
 *   TagEditor            - the editable chip box
 *   TagChips             - read-only chips for list rows and cards
 *   useTagSuggestions    - fetch (and process-cache) the company's list
 *   clearTagSuggestionCache - call after an admin edits the list
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiGet } from '../lib/api';

/**
 * Process-level cache. The suggestion list is small, changes about twice a year,
 * and is needed on several screens - refetching it per mount would be silly.
 * @type {string[]|null}
 */
let suggestionCache = null;

/** Drop the cache so the next screen refetches (used after an admin saves). */
export function clearTagSuggestionCache() {
  suggestionCache = null;
}

/**
 * Load the company's tag suggestion list.
 * Fails soft: on any error you simply get no suggestion chips, and free-form
 * tagging - the part that always works - is unaffected.
 * @returns {string[]}
 */
export function useTagSuggestions() {
  const [suggestions, setSuggestions] = useState(suggestionCache || []);
  useEffect(() => {
    if (suggestionCache) { setSuggestions(suggestionCache); return undefined; }
    let alive = true;
    apiGet('/customer/tag-suggestions')
      .then((list) => {
        suggestionCache = Array.isArray(list) ? list : [];
        if (alive) setSuggestions(suggestionCache);
      })
      .catch(() => { /* no suggestions is a fine outcome */ });
    return () => { alive = false; };
  }, []);
  return suggestions;
}

/**
 * Read-only tag chips, for list rows and summary cards.
 * @param {object} props
 * @param {string[]} [props.tags]
 * @param {number} [props.max] - show at most this many, then "+N"
 */
export function TagChips({ tags, max = 6 }) {
  const list = Array.isArray(tags) ? tags : [];
  if (list.length === 0) return null;
  const shown = list.slice(0, max);
  const extra = list.length - shown.length;
  return (
    <div style={T.chipRow}>
      {shown.map((t) => <span key={t} style={T.chipStatic}>{t}</span>)}
      {extra > 0 && <span style={{ ...T.chipStatic, opacity: 0.7 }}>+{extra}</span>}
    </div>
  );
}

/**
 * The editable tag box.
 *
 * @param {object} props
 * @param {string[]} props.tags - current tags
 * @param {string[]} [props.suggestions] - one-click chips
 * @param {(next: string[]) => void} props.onChange - called with the full new list
 * @param {boolean} [props.disabled] - true while a save is in flight
 */
export function TagEditor({ tags, suggestions = [], onChange, disabled }) {
  // Memoized so the add/remove callbacks below don't take a brand-new array
  // dependency on every render (react-hooks/exhaustive-deps).
  const list = useMemo(() => (Array.isArray(tags) ? tags : []), [tags]);
  const [draft, setDraft] = useState('');

  /** Add a tag unless it is blank or already there (case-insensitively). */
  const add = useCallback((raw) => {
    const tag = String(raw || '').trim().replace(/\s+/g, ' ');
    if (!tag) return;
    if (list.some((t) => t.toLowerCase() === tag.toLowerCase())) return;
    onChange([...list, tag]);
  }, [list, onChange]);

  const remove = useCallback((tag) => {
    onChange(list.filter((t) => t !== tag));
  }, [list, onChange]);

  function onKeyDown(e) {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();          // Enter inside a form must not submit it
      add(draft);
      setDraft('');
    } else if (e.key === 'Backspace' && draft === '' && list.length) {
      // Empty box + backspace pulls the last chip back off, like every other
      // tag input people have used.
      onChange(list.slice(0, -1));
    }
  }

  const unused = suggestions.filter(
    (s) => !list.some((t) => t.toLowerCase() === s.toLowerCase())
  );

  return (
    <div style={{ opacity: disabled ? 0.6 : 1 }}>
      <div style={T.box}>
        {list.map((t) => (
          <span key={t} style={T.chip}>
            {t}
            <button
              type="button"
              style={T.chipX}
              disabled={disabled}
              aria-label={`Remove tag ${t}`}
              title={`Remove ${t}`}
              onClick={() => remove(t)}
            >
              &times;
            </button>
          </span>
        ))}
        <input
          style={T.input}
          value={draft}
          disabled={disabled}
          placeholder={list.length ? 'Add a tag...' : 'Type a tag and press Enter'}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          onBlur={() => { if (draft.trim()) { add(draft); setDraft(''); } }}
          aria-label="Add a tag"
        />
      </div>

      {unused.length > 0 && (
        <div style={T.suggestWrap}>
          <span style={T.suggestLabel}>Suggested:</span>
          {unused.map((s) => (
            <button
              key={s}
              type="button"
              style={T.chipSuggest}
              disabled={disabled}
              onClick={() => add(s)}
              title={`Add ${s}`}
            >
              + {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const chipBase = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.25rem',
  fontSize: '0.75rem',
  lineHeight: 1.4,
  padding: '2px 8px',
  borderRadius: '9999px',
  whiteSpace: 'nowrap',
};

const T = {
  box: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '0.35rem',
    padding: '0.4rem 0.5rem',
    border: '1px solid var(--color-border, #E5E7EB)',
    borderRadius: '8px',
    backgroundColor: 'var(--surface-card, #fff)',
    minHeight: '2.25rem',
  },
  chip: {
    ...chipBase,
    backgroundColor: 'var(--surface-page, #F3F4F6)',
    color: 'var(--text-primary, #111)',
    border: '1px solid var(--color-border, #E5E7EB)',
  },
  chipStatic: {
    ...chipBase,
    marginRight: '0.25rem',
    backgroundColor: 'var(--surface-page, #F3F4F6)',
    color: 'var(--text-secondary, #6B7280)',
    border: '1px solid var(--color-border, #E5E7EB)',
  },
  chipSuggest: {
    ...chipBase,
    cursor: 'pointer',
    backgroundColor: 'transparent',
    color: 'var(--color-primary-600, #2563EB)',
    border: '1px dashed var(--color-border, #E5E7EB)',
  },
  chipX: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
    fontSize: '0.95rem',
    lineHeight: 1,
    color: 'var(--text-secondary, #6B7280)',
  },
  chipRow: { display: 'flex', flexWrap: 'wrap', gap: '0.25rem', marginTop: '0.25rem' },
  input: {
    flex: '1 1 130px',
    minWidth: '110px',
    border: 'none',
    outline: 'none',
    fontSize: '0.85rem',
    padding: '0.15rem 0',
    backgroundColor: 'transparent',
    color: 'var(--text-primary, #111)',
  },
  suggestWrap: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '0.35rem',
    marginTop: '0.5rem',
  },
  suggestLabel: {
    fontSize: '0.7rem',
    textTransform: 'uppercase',
    letterSpacing: '0.03em',
    color: 'var(--text-tertiary, #9CA3AF)',
  },
};

export default TagEditor;
